import { GetStaticProps } from "next"
import { getSubscriptionNotes } from "api_routes/discover"
import { TrendingResp } from "@/types/farcaster"

import { ReactElement, useEffect } from "react"
import DiscoverLayout from "components/layouts/DiscoverLayout"
import DiscoverFeed from "components/Discover/DiscoverFeed"

import { ParsedUrlQuery } from "querystring"
import useUser from "hooks/useUser"
import {
  LoadingContainer,
  TextContainer,
} from "components/Discover/TextContainer"
import Button from "components/Button"
import Link from "next/link"
import { useRouter } from "next/router"
import { DiscoverCursor } from "@types"

type StaticPropsType = {
  data?: TrendingResp
  errorMsg?: any
}

const fetchData = async (
  params: { cursor?: DiscoverCursor; page?: number; tag?: string },
  userId: string
) => {
  const res = await getSubscriptionNotes(params.page || 1, userId)
  return res
}

interface Params extends ParsedUrlQuery {
  userIdOrWallet: string
}

export const getStaticProps: GetStaticProps<StaticPropsType, Params> = async (
  ctx
) => {
  const vals = ctx
  const params = vals.params

  const id = params?.userIdOrWallet?.[0]

  if (!id || typeof id !== "string") {
    return {
      props: { data: [] },
      notFound: false,
      revalidate: 10,
    }
  }

  console.log("Running with ID", id)

  try {
    const res = await fetchData({ page: 1 }, id)

    if ("msg" in res) {
      throw res.msg
    }

    return {
      props: { data: res },
      revalidate: 10,
      notFound: false,
    }
  } catch (err) {
    console.error("Error fetching subscription notes", err)

    return {
      props: { data: [] },
      notFound: false,
      revalidate: 10,
    }
  }
}

export async function getStaticPaths() {
  return {
    paths: [],
    fallback: true,
  }
}

export default function DiscoverSubscriptionFeed({
  data,
  errorMsg,
}: StaticPropsType) {
  const router = useRouter()
  const user = useUser()

  useEffect(() => {
    if (!router.isReady || !user.loggedIn) return
    if (!router.query.userIdOrWallet && user.loggedIn) {
      console.log("Redirecting user to user ID route...", user.id)
      router.push(`/discover/feed/for-you/subscriptions/${user.id}`)
      return
    }
  }, [router.isReady, user.loggedIn])

  console.log("USER LOGGED IN", user)

  if (!user.loggedIn) {
    return (
      <TextContainer>
        <div>
          <div>
            You aren't logged in to Paragraph. <br />
            Login or create an account to view all newsletters you're subscribed
            to.
          </div>
          <div className="flex flex-row justify-center space-x-6 py-8">
            <Link href="/login">
              <Button text="Login" type="button"></Button>
            </Link>

            <Link href="/register">
              <Button text="Register" type="button"></Button>
            </Link>
          </div>
        </div>
      </TextContainer>
    )
  }

  const id = router.query.userIdOrWallet?.[0]
  const isValidId = typeof id === "string"

  return (
    <>
      {!router.isReady && <LoadingContainer />}
      {router.isReady && isValidId && (
        <DiscoverFeed
          emptyMsg={
            <div>
              All posts from publications you're subscribed to will appear here.{" "}
              <br />
              It doesn't look like you're subscribed to any.
            </div>
          }
          data={data}
          fetchData={(params) => fetchData(params, id)}
          errorMsg={errorMsg}
        />
      )}
    </>
  )
}

DiscoverSubscriptionFeed.getLayout = function getLayout(page: ReactElement) {
  return <DiscoverLayout>{page}</DiscoverLayout>
}
